import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Car, Check, Search, X } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { layout4ThemeStyle } from '@/layouts/layout-4/theme';
import '@/layouts/layout-4/styles.css';

interface Vehicle {
  id: string | number;
  brand: string;
  model: string;
  version: string;
  modelYear: string | number;
  price: number;
  mileage: number;
  fuelType: string;
  photos: string[];
}

interface VehicleSelectorProps {
  vehicles: Vehicle[];
  selectedVehicle?: Vehicle;
  onSelect: (vehicle: Vehicle) => void;
  placeholder?: string;
  disabled?: boolean;
  serviceImage?: string;
  serviceTitle?: string;
  layoutVariant?: 'default' | 'layout-4';
}

export const VehicleSelector = ({
  vehicles,
  selectedVehicle,
  disabled,
  onSelect,
  placeholder = "Selecione um veículo",
  serviceImage,
  serviceTitle,
  layoutVariant = 'default',
}: VehicleSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { colors } = useTheme();
  const isLayout4 = layoutVariant === 'layout-4';
  const modalThemeStyle = isLayout4 ? layout4ThemeStyle(colors) : undefined;

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const handleSelect = (vehicle: Vehicle) => {
    onSelect(vehicle);
    setIsOpen(false);
    setSearchTerm('');
  };

  const getVehicleImage = (vehicle: Vehicle) => {
    return vehicle.photos && vehicle.photos.length > 0
      ? vehicle.photos[0]
      : '/placeholder.svg';
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const searchText = searchTerm.toLowerCase();
    return (
      vehicle.brand.toLowerCase().includes(searchText) ||
      vehicle.model.toLowerCase().includes(searchText) ||
      vehicle.version.toLowerCase().includes(searchText) ||
      vehicle.modelYear.toString().includes(searchText)
    );
  });

  return (
    <Dialog open={!disabled && isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {isLayout4 ? (
          <div
            className={`wl4-vehicle-picker ${disabled ? 'is-disabled' : ''}`}
            style={modalThemeStyle}
          >
            {selectedVehicle ? (
              <>
                <div className="wl4-vehicle-picker-thumb">
                  <img
                    src={getVehicleImage(selectedVehicle)}
                    alt={`${selectedVehicle.brand} ${selectedVehicle.model}`}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder.svg';
                    }}
                  />
                </div>
                <div className="wl4-vehicle-picker-body">
                  <p className="wl4-vehicle-picker-title">Veículo selecionado</p>
                  <p className="wl4-vehicle-picker-name">
                    {selectedVehicle.brand} {selectedVehicle.model}
                  </p>
                  <p className="wl4-vehicle-picker-hint">{selectedVehicle.version}</p>
                </div>
                <span className="wl4-vehicle-picker-price">{formatPrice(selectedVehicle.price)}</span>
              </>
            ) : (
              <>
                <div className="wl4-vehicle-picker-thumb">
                  <Car className="h-6 w-6 text-[var(--wl4-muted)]" />
                </div>
                <div className="wl4-vehicle-picker-body">
                  <p className="wl4-vehicle-picker-title">{placeholder}</p>
                  <p className="wl4-vehicle-picker-name">Clique para selecionar</p>
                  <p className="wl4-vehicle-picker-hint">Escolha um veículo do estoque</p>
                </div>
              </>
            )}
          </div>
        ) : (
        <div
          className={`flex items-center gap-4 p-4 border rounded-lg transition-all ${disabled ? 'bg-muted cursor-not-allowed border-border' : 'cursor-pointer hover:bg-muted/50 border-border hover:border-primary/50'}`}
        >
          {selectedVehicle ? (
            <>
              {/* Vehicle Image */}
              <div className="relative flex-shrink-0">
                <img
                  src={getVehicleImage(selectedVehicle)}
                  alt={`${selectedVehicle.brand} ${selectedVehicle.model}`}
                  className="w-16 h-12 object-cover rounded-md"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder.svg';
                  }}
                />
              </div>

              {/* Vehicle Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <h4 className="font-semibold text-sm truncate">
                    {selectedVehicle.brand} {selectedVehicle.model}
                  </h4>
                  <span className="text-sm font-bold whitespace-nowrap" style={{ color: primaryColor }}>
                    {formatPrice(selectedVehicle.price)}
                  </span>
                </div>

                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {selectedVehicle.version}
                </p>

                {/* Minimalist Details */}
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <span>{selectedVehicle.modelYear}</span>
                  <span>•</span>
                  <span>{formatMileage(selectedVehicle.mileage)}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Placeholder Image */}
              <div className="relative flex-shrink-0">
                <div className="w-16 h-12 bg-muted/50 rounded-md flex items-center justify-center">
                  <Car className="h-6 w-6 text-muted-foreground/40" />
                </div>
              </div>

              {/* Placeholder Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <h4 className="font-semibold text-sm truncate">
                    {placeholder}
                  </h4>
                  <div className="h-4 bg-muted/50 rounded w-20" style={{ background: primaryColor, opacity: 0.10 }}></div>
                  {/* <span className="text-sm font-bold whitespace-nowrap" style={{ color: primaryColor }}>
                    R$ 0,00
                  </span> */}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  CLIQUE PARA SELECIONAR
                </p>

                {/* Minimalist Details */}
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <div className="h-4 bg-muted/50 rounded w-28"></div>
                </div>
              </div>
            </>
          )}
        </div>
        )}
      </DialogTrigger>

      <DialogContent
        className={
          isLayout4
            ? 'layout-4 wl4-service-modal max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
            : 'max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
        }
        style={modalThemeStyle}
      >
        {(isLayout4 || serviceImage) && (
          <DialogTitle className="sr-only">
            {serviceTitle || 'Selecionar Veículo'}
          </DialogTitle>
        )}

        {isLayout4 ? (
          <>
            <div className="wl4-service-modal-head">
              <div className="wl4-service-modal-head-icon">
                <Car className="h-5 w-5" />
              </div>
              <div className="wl4-service-modal-head-text">
                <h2>Selecionar veículo</h2>
                <p>Escolha um veículo do estoque</p>
              </div>
              <button
                type="button"
                className="wl4-service-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="wl4-vehicle-search relative">
              <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--wl4-muted)] z-10" />
              <Input
                placeholder="Pesquisar marca, modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="wl4-service-input"
              />
            </div>
          </>
        ) : serviceImage ? (
          <div className="relative aspect-[280/100] overflow-hidden flex-shrink-0">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-10 w-10 bg-black/5 hover:bg-black/10 backdrop-blur-sm border border-black/10 text-black z-20"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-5 w-5 text-black" />
            </Button>

            {/* Title and Icon Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-black/5 backdrop-blur-sm border border-black/10">
                  <Car className="h-7 w-7 text-black/80" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-black">
                    Selecionar Veículo
                  </h2>
                  <p className="text-sm text-black/80">
                    Escolha um veículo do estoque
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
            {/* Hidden DialogTitle for accessibility */}
            <DialogTitle className="sr-only">
              {serviceTitle || 'Selecionar Veículo'}
            </DialogTitle>

            {/* Gradient Overlay */}
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}05)` }} />

            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-10 w-10 bg-background/10 hover:bg-background/20 backdrop-blur-sm border border-border/20 z-20"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Title and Icon Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-background/10 backdrop-blur-sm border border-border/20">
                  <Car className="h-7 w-7" style={{ color: primaryColor }} />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-foreground">
                    Selecionar Veículo
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Escolha um veículo do estoque
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLayout4 && (
        <div className="px-6 pb-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {filteredVehicles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6 space-y-2">
              <p className="font-medium text-foreground">
                Nenhum veículo encontrado
              </p>
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Tente buscar por outro termo' : 'Não há veículos disponíveis'}
              </p>
            </div>
          ) : (
            <div>
              {filteredVehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className={
                    isLayout4
                      ? `wl4-vehicle-list-item ${selectedVehicle?.id === vehicle.id ? 'is-selected' : ''}`
                      : `group relative flex items-center gap-4 p-4 cursor-pointer transition-all ${selectedVehicle?.id === vehicle.id ? 'bg-muted/75' : 'hover:bg-muted/50'}`
                  }
                  onClick={() => handleSelect(vehicle)}
                >
                  {isLayout4 ? (
                    <>
                      <img
                        src={getVehicleImage(vehicle)}
                        alt={`${vehicle.brand} ${vehicle.model}`}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder.svg';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <h4 className="font-semibold text-sm truncate">
                            {vehicle.brand} {vehicle.model}
                          </h4>
                          <span className="text-sm font-bold whitespace-nowrap text-[var(--wl4-accent)]">
                            {formatPrice(vehicle.price)}
                          </span>
                        </div>
                        <p className="text-xs text-[var(--wl4-muted)] truncate mt-0.5">{vehicle.version}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-[var(--wl4-muted)]">
                          <span>{vehicle.modelYear}</span>
                          <span>•</span>
                          <span>{formatMileage(vehicle.mileage)}</span>
                        </div>
                      </div>
                      {selectedVehicle?.id === vehicle.id && (
                        <Check className="h-5 w-5 text-[var(--wl4-accent)] shrink-0" />
                      )}
                    </>
                  ) : (
                    <>
                  {/* Vehicle Image */}
                  <div className="relative flex-shrink-0">
                    <img
                      src={getVehicleImage(vehicle)}
                      alt={`${vehicle.brand} ${vehicle.model}`}
                      className="w-16 h-12 object-cover rounded-md"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder.svg';
                      }}
                    />

                    {/* Check Icon for Selected - Top Left */}
                    {selectedVehicle?.id === vehicle.id && (
                      <div className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: primaryColor }}>
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Vehicle Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <h4 className="font-semibold text-sm truncate">
                        {vehicle.brand} {vehicle.model}
                      </h4>
                      <span className="text-sm font-bold whitespace-nowrap" style={{ color: primaryColor }}>
                        {formatPrice(vehicle.price)}
                      </span>
                    </div>

                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {vehicle.version}
                    </p>

                    {/* Minimalist Details */}
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <span>{vehicle.modelYear}</span>
                      <span>•</span>
                      <span>{formatMileage(vehicle.mileage)}</span>
                      {vehicle.fuelType && (
                        <>
                          <span>•</span>
                          <span>{vehicle.fuelType}</span>
                        </>
                      )}
                    </div>
                  </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};